/**
 * ResumoPreAnalise
 *
 * Componente de resumo pré-análise que exibe um resumo consolidado da petição,
 * documentos enviados e agentes selecionados, e solicita a confirmação final do
 * usuário antes de iniciar a análise multi-agent.
 *
 * CONTEXTO DE NEGÓCIO:
 * - Este componente implementa o requisito da TAREFA-061: solicitar input do
 *   usuário entre etapas do fluxo de análise de petição.
 * - Deve ser compacto, legível e altamente documentado para manutenção por IAs.
 */

import React, { useEffect, useState } from 'react';
import { Users, Briefcase, FileText } from 'lucide-react';
import type { AgentesSelecionados } from '../tipos/tiposPeticao';
import { listarDocumentosPeticao } from '../servicos/servicoApiPeticoes';

interface DocumentoEnviado {
  documento_id?: string;
  nome_arquivo?: string;
  id_documento?: string;
  nome_arquivo_original?: string;
}

interface DadosDocumentos extends Record<string, unknown> {
  documentos_enviados?: DocumentoEnviado[];
  nome_arquivo_peticao?: string;
  nome_arquivo?: string;
  peticao_nome_arquivo?: string;
  arquivo_peticao_nome?: string;
}

interface Props {
  peticaoId: string;
  documentosEnviados: string[];
  documentosSugeridosCount?: number;
  agentesSelecionados: AgentesSelecionados;
  onConfirmar: () => void;
  onEditar?: () => void;
}

/**
 * ResumoPreAnalise (versão aprimorada)
 * - Apresenta informações em cards distintos (Petição, Documentos, Agentes)
 * - Destaca contadores e torna os botões de ação mais visíveis
 * - Mantém a API (props) compatível com a versão anterior
 */
export function ResumoPreAnalise({
  peticaoId,
  documentosEnviados,
  documentosSugeridosCount = 0,
  agentesSelecionados,
  onConfirmar,
  onEditar,
}: Props): JSX.Element {
  const totalAdvogados = agentesSelecionados.advogados.length;
  const totalPeritos = agentesSelecionados.peritos.length;
  const [documentoIdParaNome, setDocumentoIdParaNome] = useState<Record<string, string>>({});
  const [nomePeticao, setNomePeticao] = useState<string | null>(null);
  const [documentosEnviadosApi, setDocumentosEnviadosApi] = useState<string[]>([]);
  const [totalEnviadosApi, setTotalEnviadosApi] = useState<number>(0);

  useEffect(() => {
    let ativo = true;
    (async () => {
      if (!peticaoId) return;
      try {
        const resp = await listarDocumentosPeticao(peticaoId);
        const dados = resp.data as DadosDocumentos & { total_enviados?: number };

        // Tentar extrair nome da petição (caso o backend forneça um campo explícito)
        const possiveisCamposNome = ['nome_arquivo_peticao', 'nome_arquivo', 'peticao_nome_arquivo', 'arquivo_peticao_nome'];
        for (const campo of possiveisCamposNome) {
          if (dados[campo]) {
            if (ativo) setNomePeticao(dados[campo] as string);
            break;
          }
        }

        // CORREÇÃO: Usar a lista de documentos retornada pela API, não a prop
        // Isso garante que temos a lista completa (ZIP + complementares)
        const mapa: Record<string, string> = {};
        const idsDocumentos: string[] = [];
        if (Array.isArray(dados.documentos_enviados)) {
          // documentos_enviados pode ser array de objetos com nome_arquivo
          dados.documentos_enviados.forEach((d: DocumentoEnviado) => {
            if (d.documento_id && d.nome_arquivo) {
              mapa[d.documento_id] = d.nome_arquivo;
              idsDocumentos.push(d.documento_id);
            }
            // alguns backends usam chaves diferentes
            if (d.id_documento && d.nome_arquivo_original) {
              mapa[d.id_documento] = d.nome_arquivo_original;
              if (!idsDocumentos.includes(d.id_documento)) {
                idsDocumentos.push(d.id_documento);
              }
            }
          });
        }
        
        if (ativo) {
          setDocumentoIdParaNome(mapa);
          setDocumentosEnviadosApi(idsDocumentos);
          // Usar total_enviados da API se disponível, senão usar o tamanho do array
          setTotalEnviadosApi(dados.total_enviados ?? idsDocumentos.length);
        }
      } catch (err) {
        console.warn('[ResumoPreAnalise] Falha ao listar documentos da petição:', err);
      }
    })();
    return () => { ativo = false; };
  }, [peticaoId]);

  return (
    <section className="space-y-6 rounded-lg border border-gray-200 bg-white p-6">
      <header className="flex items-center justify-between">
        <h3 className="text-lg font-bold">Resumo antes de Iniciar a Análise</h3>
        <span className="text-sm text-gray-500">Revise e confirme os dados abaixo</span>
      </header>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        {/* Petição */}
        <div className="rounded border border-gray-100 bg-gray-50 p-4">
          <div className="flex items-start gap-3">
            <FileText className="mt-1 size-6 text-primary-600" />
            <div>
              <div className="text-sm text-gray-600">Petição</div>
              <div className="mt-1 font-medium text-gray-900">{nomePeticao || (peticaoId ? `ID: ${peticaoId}` : '— (não informado)')}</div>
            </div>
          </div>
        </div>

        {/* Documentos */}
        <div className="rounded border border-gray-100 bg-gray-50 p-4">
          <div className="flex items-start gap-3">
            <Briefcase className="mt-1 size-6 text-primary-600" />
            <div>
              <div className="text-sm text-gray-600">Documentos</div>
              <div className="mt-1 flex items-center gap-3">
                {/* CORREÇÃO: Usar dados da API em vez da prop para garantir lista completa */}
                <div className="text-sm font-medium text-gray-900">Enviados: {totalEnviadosApi || documentosEnviados.length}</div>
                <div className="text-sm font-medium text-gray-900">Sugeridos: {documentosSugeridosCount}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Agentes */}
        <div className="rounded border border-gray-100 bg-gray-50 p-4">
          <div className="flex items-start gap-3">
            <Users className="mt-1 size-6 text-primary-600" />
            <div>
              <div className="text-sm text-gray-600">Agentes Selecionados</div>
              <div className="mt-1 text-sm text-gray-900">
                <span className="font-medium">Advogados:</span> {totalAdvogados} • <span className="font-medium">Peritos:</span> {totalPeritos}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Listas detalhadas (visível em telas maiores) */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div className="rounded border border-gray-100 bg-white p-4">
          <h4 className="mb-2 font-medium text-gray-800">Advogados</h4>
          {totalAdvogados === 0 ? (
            <p className="text-sm text-gray-600">Nenhum advogado selecionado</p>
          ) : (
            <ul className="space-y-1 text-sm text-gray-700">
              {agentesSelecionados.advogados.map((a) => (
                <li key={a} className="flex items-center gap-3">
                  <div className="flex size-7 items-center justify-center rounded-full bg-primary-100 text-xs font-semibold text-primary-700">{a.slice(0,1).toUpperCase()}</div>
                  <span>{a}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="rounded border border-gray-100 bg-white p-4">
          <h4 className="mb-2 font-medium text-gray-800">Peritos</h4>
          {totalPeritos === 0 ? (
            <p className="text-sm text-gray-600">Nenhum perito selecionado</p>
          ) : (
            <ul className="space-y-1 text-sm text-gray-700">
              {agentesSelecionados.peritos.map((p) => (
                <li key={p} className="flex items-center gap-3">
                  <div className="flex size-7 items-center justify-center rounded-full bg-primary-100 text-xs font-semibold text-primary-700">{p.slice(0,1).toUpperCase()}</div>
                  <span>{p}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Lista de documentos anexados (com nomes quando disponíveis) */}
      <div className="rounded border border-gray-100 bg-white p-4">
        <h4 className="mb-2 font-medium text-gray-800">Documentos anexados</h4>
        {/* CORREÇÃO: Usar lista da API para garantir que todos os documentos apareçam */}
        {(documentosEnviadosApi.length > 0 ? documentosEnviadosApi : documentosEnviados).length === 0 ? (
          <p className="text-sm text-gray-600">Nenhum documento anexado</p>
        ) : (
          <ul className="space-y-1 text-sm text-gray-700">
            {(documentosEnviadosApi.length > 0 ? documentosEnviadosApi : documentosEnviados).map((id) => (
              <li key={id} className="flex items-center gap-3">
                <FileText className="size-4 text-gray-500" />
                <span>{documentoIdParaNome[id] || id}</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-end">
        {onEditar && (
          <button
            type="button"
            onClick={onEditar}
            className="w-full rounded-md border border-primary-600 bg-transparent px-4 py-3 text-sm text-primary-600 hover:bg-primary-50 sm:w-auto"
            aria-label="Editar seleção de agentes e documentos"
          >
            ✏️ Editar
          </button>
        )}

        <button
          type="button"
          onClick={onConfirmar}
          className="w-full rounded-md bg-primary-600 px-6 py-3 text-sm font-semibold text-white hover:bg-primary-700 sm:w-auto"
          aria-label="Confirmar e iniciar análise"
        >
          ✅ Confirmar e Iniciar Análise
        </button>
      </div>
    </section>
  );
}

export default ResumoPreAnalise;
